import { config } from '@uhg-abyss/web/tools/config';
import { find } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Constants, GQL_API_NAMES } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { isMissingParameters } from '../../errors/helpers';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { isPolicyIdPresentForAOESuppression } from '../../utils/common';
import { getProviderFilterOptions } from '../../utils/providerSearch.utils';
import {
  getAdditionalAttributes,
  getAoeFiltersFromLagoon,
  getCurrentMember,
} from '../../utils/user.utils';
import { useCustomQuery } from '../useCustomQuery';
import { useFeatureFlag } from '../useFeatureFlag';
import { useLagoon } from '../useLagoon';
import { useProviderSearchVariables } from '../useProviderSearch/useProviderSearch';
import GET_PROVIDER_FILTER_COUNT from './ProviderFilterCount.graphql';

export const useProviderFilterCount = (options) => {
  const [filterOptions, setFilterOptions] = useState({});
  const [queryResult, queryAction] = useCustomQuery(GET_PROVIDER_FILTER_COUNT, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'providerSearch',
    /* istanbul ignore next */
    onCompleted: (result) => {
      const data = result?.data?.providerFilterCount;
      if (options?.onCompleted) {
        options.onCompleted(data);
      }
      setFilterOptions(data);
    },
  });

  const parsedResult = {
    ...queryResult,
    data: filterOptions,
  };

  return [parsedResult, queryAction];
};

export const useParsedProviderFilters = (params) => {
  const { t } = useTranslation();
  const [providerFilters, getProviderFilters] = useProviderFilterCount({});
  const featureSuppression = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const suppressedAOECodesRow = find(featureSuppression, {
    DNT_feature:
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.GENDER_AFFIRMING_AOE_CODES,
    active: true,
  });
  const {
    sectionType,
    providerType,
    validPlanVariationCode,
    providerSearchVariables: variables,
    featureFlags,
    canMakeProviderSearchQuery: canMakeProviderFilterCountQuery,
    requiredParamsForProviderFilterCountQuery: requiredParams,
  } = useProviderSearchVariables(params);

  const [enableAgesTreatedFilterFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_AGES_TREATED_FILTER,
  ]);
  const aoeFiltersLagoon = useLagoon(Constants.LAGOON_TABLE.AOE_FILTERS)();
  const aoeFilters = getAoeFiltersFromLagoon(
    aoeFiltersLagoon,
    providerType,
    enableAgesTreatedFilterFlag
  );

  const additionalAttributesLagoon = useLagoon('additional-attributes')();
  const currentMember = getCurrentMember();
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      storeLob: state.lob,
      chipValue: state.chipValue,
    }))
  );

  const additionalAttributes =
    getAdditionalAttributes(
      additionalAttributesLagoon,
      currentMember,
      coverageType,
      providerType,
      GQL_API_NAMES.PROVIDER_FILTER_COUNT
    ) || [];

  useEffect(() => {
    if (!canMakeProviderFilterCountQuery) {
      return;
    }
    if (!(providerType || sectionType)) {
      return;
    }
    if (!validPlanVariationCode) {
      throw new Error('Plan Variation Code is required');
    }
    if (isMissingParameters('Provider Filter Count', requiredParams)) {
      return;
    }

    variables.additionalAttributes = additionalAttributes;
    variables.aoeFiltersTable = aoeFilters;
    variables.suppressAOECodes = isPolicyIdPresentForAOESuppression(
      coverageType,
      suppressedAOECodesRow,
      currentMember?.policyNumber ?? ''
    );
    getProviderFilters({ variables });
  }, [
    JSON.stringify(variables),
    JSON.stringify(additionalAttributes),
    canMakeProviderFilterCountQuery,
  ]);

  return {
    error: providerFilters?.error,
    isLoading: providerFilters?.isLoading,
    totalCount: providerFilters?.data?.totalCount || 0,
    providerFilterList: getProviderFilterOptions(
      providerFilters?.data?.providerFilterList,
      featureFlags,
      coverageType,
      t
    ),
  };
};
